import LoginRedirect from '@eui-page/auth0-login-redirect';
import LogoutRedirect from '@eui-page/auth0-logout-redirect';
import RouteNames from './route-names';
import SignUpLayout from '../components/layouts/sign-up-layout';
import MainLayout from '../components/layouts/main-layout';
import PrimaryLayout from '../components/layouts/primary-layout';
import DashboardLayout from '../components/layouts/dashboard-layout';
import store from '../store';
import { LOGIN_THEME, ROUTE_BASE } from '@/utils/constants';
import { getEnv } from 'ecx.saigon.ui.common';
import resolveLocalRoute from '@/utils/resolve-local-route';

export default [
  {
    path: '/login',
    name: RouteNames.login,
    component: LoginRedirect,
    async beforeEnter(to, from, next) {
      const env = await getEnv();
      const windowPathname = window.location.pathname.replace(ROUTE_BASE, '');
      const windowSearch = window.location.search;
      to.query.fromPath = `${windowPathname}${windowSearch}`;
      to.meta.baseUrl = resolveLocalRoute(env.VUE_APP_CORE_ROUTE);
      next();
    },
    props: (route) => {
      return {
        redirectLoginOptions: {
          appState: { fromPath: route.query.fromPath },
          redirect_uri: route.query.redirectUri || route.meta.baseUrl,
          fragment: `&theme=${LOGIN_THEME}&baseUrl=${route.meta.baseUrl}`
        }
      };
    }
  },
  {
    path: '/logout',
    name: RouteNames.logout,
    meta: { skipAuthentication: true },
    props: { returnTo: `${window.location.origin}/core/login` },
    component: LogoutRedirect,
    beforeEnter: (to, from, next) => {
      store.commit('user/clearUserData');
      next();
    }
  },
  {
    path: '/sign-up',
    name: RouteNames.signUp,
    meta: { skipAuthentication: true, layout: SignUpLayout },
    component: () => import('../widgets/sign-up')
  },
  {
    path: '/validate-email/:token/tokenType/:tokenType',
    name: RouteNames.signUpVerification,
    meta: { skipAuthentication: true, layout: SignUpLayout },
    props: true,
    component: () => import('../widgets/sign-up-pin')
  },
  {
    path: '/account-already-exists',
    name: RouteNames.accountExists,
    meta: { skipAuthentication: true, layout: SignUpLayout },
    component: () => import('../widgets/account-exists')
  },
  {
    path: '/reset-password',
    name: RouteNames.resetPassword,
    meta: { skipAuthentication: true, layout: SignUpLayout },
    component: () => import('../widgets/reset-password')
  },
  {
    path: '/reset-password/:token',
    name: RouteNames.resetPasswordConfirmation,
    props: true,
    meta: { skipAuthentication: true, layout: SignUpLayout },
    component: () => import('../widgets/reset-password-confirmation')
  },
  {
    path: '/terms-and-conditions',
    name: RouteNames.termsAndConditions,
    meta: { skipAuthentication: true, layout: DashboardLayout },
    beforeEnter: () => {
      const theme = store.getters['user/theme']();
      window.location = theme.defaultBrand?.legal?.termsAndConditionsLink;
      return false;
    }
  },
  {
    path: '/privacy-policy',
    name: RouteNames.privacyPolicy,
    meta: { skipAuthentication: true, layout: DashboardLayout },
    beforeEnter: () => {
      const theme = store.getters['user/theme']();
      window.location = theme.defaultBrand?.legal?.privacyPolicyLink;
      return false;
    }
  },
  {
    path: '/novated-calculator-assumptions',
    name: RouteNames.novatedCalculatorAssumptions,
    meta: { skipAuthentication: true, layout: DashboardLayout },
    component: () => import('../widgets/novated-calculator-assumptions')
  },
  // exact matches to 'skipAuthentication' routes will trigger before
  // matching the user-hydration base route
  {
    path: '',
    alias: '/',
    redirect: { name: RouteNames.dashboard },
    component: () => import('../widgets/hydration'),
    children: [
      {
        path: 'document-signed',
        name: RouteNames.documentSigned,
        meta: { layout: MainLayout },
        component: () => import('../widgets/docusign-thank-you')
      },
      {
        path: 'dashboard',
        name: RouteNames.dashboard,
        meta: { layout: DashboardLayout },
        component: () => import('../widgets/dashboard')
      },
      {
        path: 'leases/:leaseId',
        name: RouteNames.lease,
        props: true,
        meta: { layout: DashboardLayout },
        component: () => import('../widgets/lease-detail')
      },
      {
        path: '/budgets',
        name: RouteNames.budgets,
        props: true,
        meta: { layout: DashboardLayout },
        component: () => import('../widgets/budget-summary'),
        children: [
          {
            path: ':budgetCategory',
            name: RouteNames.budgetDetail,
            props: true,
            meta: { layout: DashboardLayout },
            component: () => import('../widgets/budget-detail')
          }
        ]
      },
      {
        path: 'claims',
        name: RouteNames.claimsLanding,
        meta: { layout: MainLayout },
        component: () => import('../widgets/claims-landing')
      },
      {
        path: 'claims/history',
        name: RouteNames.claims,
        meta: { layout: MainLayout },
        component: () => import('../widgets/claims')
      },
      {
        path: 'claims/new',
        name: RouteNames.claimsNew,
        meta: { layout: MainLayout },
        component: () => import('../widgets/claims-new')
      },
      {
        path: 'claims/ev/new',
        name: RouteNames.claimsEvNew,
        meta: { layout: MainLayout },
        component: () => import('../widgets/claims-ev-new')
      },
      {
        path: 'hr',
        redirect: { name: RouteNames.hrApprovals },
        component: () => import('../widgets/hr'),
        children: [
          {
            path: 'approvals',
            name: RouteNames.hrApprovals,
            meta: { layout: MainLayout },
            component: () => import('../widgets/hr-approvals')
          },
          {
            path: 'approvals/:id',
            name: RouteNames.hrApproval,
            meta: { layout: PrimaryLayout },
            props: true,
            component: () => import('../widgets/hr-approval')
          }
        ]
      }
    ]
  }
];
